import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import AnalyticsShell from '../components/analytics/AnalyticsShell'
import DataTable, { type DataColumn } from '../components/analytics/DataTable'
import MetricCard from '../components/analytics/MetricCard'
import WidgetShell from '../components/analytics/WidgetShell'
import { calcDelta, formatCompactNumber } from '../components/analytics/analyticsTypes'
import { analyticsService, type EventLocation, type PortfolioComparisonRow, type TimeRange } from '../services/analyticsService'
import LiveEventFeed from '../components/LiveEventFeed'
import Badge from '../components/ui/Badge'
import { PlatformIcon } from '../components/icons'
import { isMapboxTokenConfigured } from '../utils/mapbox'
import { liveEventsService, type LiveEvent } from '../services/liveEventsService'
import { useAuthStore } from '../stores/authStore'
import { formatPlatform } from '../utils/platform'

const mapboxToken = (import.meta.env.VITE_MAPBOX_TOKEN || '').trim()
const hasMapboxToken = isMapboxTokenConfigured(mapboxToken)

if (hasMapboxToken) {
  mapboxgl.accessToken = mapboxToken
}

interface PortfolioCommandCenterProps {
  onSelectApp: (appId: string) => void
}

type PortfolioRange = 'live' | TimeRange
const PORTFOLIO_RANGES: PortfolioRange[] = ['live', '24h', '48h', '72h', '5d', '15d', '30d', '60d', '90d']

/** Rolling window the live map and live metric cards describe. */
const LIVE_WINDOW_MS = 30_000
/**
 * How often the buffered live stream is folded into React state. Live events
 * arrive far faster than a human can read them, so one snapshot per second is
 * the useful resolution — and it keeps render cost independent of event rate.
 */
const LIVE_FLUSH_INTERVAL_MS = 1_000
/** Hard ceiling on buffered events, so a traffic spike cannot grow unbounded. */
const LIVE_BUFFER_LIMIT = 400
/** ~1.1 km at the equator: merges repeat pings from one device into one point. */
const LIVE_GRID_PRECISION = 2

interface BufferedLiveEvent {
  event: LiveEvent
  at: number
}

interface LiveSnapshot {
  events: number
  sessions: number
  apps: number
  locations: EventLocation[]
}

const EMPTY_LIVE_SNAPSHOT: LiveSnapshot = { events: 0, sessions: 0, apps: 0, locations: [] }

/** Parse the timestamp once, on arrival, rather than on every render pass. */
function bufferLiveEvent(event: LiveEvent): BufferedLiveEvent {
  const parsed = Date.parse(event.timestamp)
  return { event, at: Number.isFinite(parsed) ? parsed : Date.now() }
}

function pruneLiveBuffer(buffer: BufferedLiveEvent[]): BufferedLiveEvent[] {
  const cutoff = Date.now() - LIVE_WINDOW_MS
  const kept = buffer.filter(entry => entry.at >= cutoff)
  return kept.length > LIVE_BUFFER_LIMIT ? kept.slice(kept.length - LIVE_BUFFER_LIMIT) : kept
}

function liveLocationApp(event: LiveEvent): NonNullable<EventLocation['apps']>[number] {
  return {
    appId: event.appId,
    appName: event.appName || 'Unknown app',
    bundleId: null,
    count: 1,
    eventName: event.eventName ?? event.type,
    lastEventAt: event.timestamp,
  }
}

/**
 * Fold the rolling window into the counts and geo clusters the map needs.
 * Pings are merged into a coarse grid so a chatty device contributes a single
 * weighted point instead of one GeoJSON feature per event.
 */
function summarizeLiveWindow(buffer: BufferedLiveEvent[]): LiveSnapshot {
  const sessions = new Set<string>()
  const apps = new Set<string>()
  const cells = new Map<string, EventLocation>()

  for (const { event } of buffer) {
    if (event.sessionId) sessions.add(event.sessionId)
    if (event.appId) apps.add(event.appId)

    const { latitude, longitude } = event
    if (typeof latitude !== 'number' || typeof longitude !== 'number') continue
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue

    const key = `${latitude.toFixed(LIVE_GRID_PRECISION)}:${longitude.toFixed(LIVE_GRID_PRECISION)}`
    const existing = cells.get(key)
    if (!existing) {
      cells.set(key, {
        lat: latitude,
        lng: longitude,
        city: event.city ?? null,
        country: event.country ?? null,
        count: 1,
        apps: [liveLocationApp(event)],
      })
      continue
    }

    existing.count += 1
    const app = existing.apps?.find(entry => entry.appId === event.appId)
    if (app) {
      app.count += 1
      if (event.timestamp > (app.lastEventAt ?? '')) app.lastEventAt = event.timestamp
    } else {
      existing.apps?.push(liveLocationApp(event))
    }
  }

  return {
    events: buffer.length,
    sessions: sessions.size,
    apps: apps.size,
    locations: [...cells.values()],
  }
}

export default function PortfolioCommandCenter({ onSelectApp }: PortfolioCommandCenterProps) {
  const { user } = useAuthStore()
  const [timeRange, setTimeRange] = useState<PortfolioRange>('live')
  const [mobilePanel, setMobilePanel] = useState<'overview' | 'map' | 'live' | 'apps'>('overview')
  const [liveSnapshot, setLiveSnapshot] = useState<LiveSnapshot>(EMPTY_LIVE_SNAPSHOT)
  const liveBuffer = useRef<BufferedLiveEvent[]>([])
  const analyticsRange: TimeRange = timeRange === 'live' ? '24h' : timeRange
  const isLive = timeRange === 'live'

  const summary = useQuery({
    queryKey: ['portfolio-summary', analyticsRange],
    queryFn: () => analyticsService.getSummary(analyticsRange),
  })

  const comparison = useQuery({
    queryKey: ['portfolio-comparison', analyticsRange],
    queryFn: () => analyticsService.getPortfolioComparison(analyticsRange),
  })

  const locations = useQuery({
    queryKey: ['portfolio-heatmap-locations', analyticsRange],
    enabled: !isLive,
    queryFn: () => analyticsService.getEventLocations(analyticsRange, { limit: 500 }),
  })

  const apps = useMemo(() => comparison.data?.apps ?? [], [comparison.data?.apps])
  const includedApps = apps.filter(app => app.includeInPortfolio)
  const totalEvents = includedApps.reduce((sum, app) => sum + app.events, 0)
  const totalSessions = includedApps.reduce((sum, app) => sum + app.sessions, 0)
  const totalCrashes = includedApps.reduce((sum, app) => sum + app.crashes, 0)
  const crashFree = totalSessions > 0 ? ((totalSessions - totalCrashes) / totalSessions) * 100 : 100

  const topMovers = useMemo(() => [...apps].sort((a, b) => b.events - a.events).slice(0, 5), [apps])
  const platformMix = useMemo(() => {
    const totals = new Map<string, { platform: string; events: number; apps: number }>()
    includedApps.forEach((app) => {
      const current = totals.get(app.platform) ?? { platform: app.platform, events: 0, apps: 0 }
      current.events += app.events
      current.apps += 1
      totals.set(app.platform, current)
    })
    return [...totals.values()].sort((a, b) => b.events - a.events)
  }, [includedApps])
  const liveWindowEvents = liveSnapshot.events
  const liveLocations = liveSnapshot.locations
  const liveActiveSessions = liveSnapshot.sessions
  const liveActiveApps = liveSnapshot.apps

  // Live ingest is deliberately decoupled from React. Events land in a ref and
  // a single timer folds the rolling window into one snapshot per second.
  // Previously every event ran two setState calls, re-parsed every buffered
  // timestamp, and rebuilt the map's GeoJSON — so a busy portfolio pushed more
  // render work onto the main thread than it could retire, and the tab locked
  // up after a while on the default view.
  useEffect(() => {
    if (!isLive) {
      liveBuffer.current = []
      setLiveSnapshot(EMPTY_LIVE_SNAPSHOT)
      return
    }

    const flush = () => {
      liveBuffer.current = pruneLiveBuffer(liveBuffer.current)
      setLiveSnapshot(summarizeLiveWindow(liveBuffer.current))
    }

    const interval = window.setInterval(flush, LIVE_FLUSH_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [isLive])

  useEffect(() => {
    if (!user || !isLive) return

    let cancelled = false

    liveEventsService.connect(user.id, user.id).catch((error) => {
      console.error('[PortfolioCommandCenter] Failed to connect live map stream', error)
    })

    liveEventsService.getRecentEvents().then((events) => {
      if (cancelled) return
      liveBuffer.current = pruneLiveBuffer([...events.map(bufferLiveEvent), ...liveBuffer.current])
      setLiveSnapshot(summarizeLiveWindow(liveBuffer.current))
    }).catch((error) => {
      console.error('[PortfolioCommandCenter] Failed to load recent live map events', error)
    })

    const unsubscribe = liveEventsService.onEvent((event) => {
      const buffer = liveBuffer.current
      buffer.push(bufferLiveEvent(event))
      // Trim eagerly so a burst between flushes cannot grow without bound.
      if (buffer.length > LIVE_BUFFER_LIMIT) {
        buffer.splice(0, buffer.length - LIVE_BUFFER_LIMIT)
      }
    })

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [user, isLive])

  const columns: DataColumn<PortfolioComparisonRow>[] = [
    {
      key: 'app',
      header: 'App',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500">
            {row.iconUrl ? <img src={row.iconUrl} alt="" className="h-full w-full rounded-2xl" /> : <PlatformIcon platform={row.platform} size="md" />}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-white">{row.appName}</p>
            <p className="truncate text-xs text-gray-500">{row.bundleId}</p>
          </div>
        </div>
      ),
    },
    { key: 'platform', header: 'Platform', render: row => formatPlatform(row.platform) },
    { key: 'sessions', header: 'Sessions', align: 'right', render: row => formatCompactNumber(row.sessions) },
    { key: 'events', header: 'Events', align: 'right', render: row => formatCompactNumber(row.events) },
    { key: 'crashFree', header: 'Crash-Free', align: 'right', render: row => `${row.crashFreeRate.toFixed(1)}%` },
    { key: 'last', header: 'Last Event', render: row => row.lastEventAt ? new Date(row.lastEventAt).toLocaleString() : 'No events' },
  ]

  return (
    <AnalyticsShell
      eyebrow="Portfolio command center"
      title="Mobile product health across every app"
      description="Track live pulse, health, release impact, and app-level movement from one exploration surface."
      actions={<PortfolioRangePicker value={timeRange} onChange={setTimeRange} />}
    >
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <MetricCard label={isLive ? 'Live Sessions' : 'Sessions'} value={isLive ? liveActiveSessions : (summary.data?.summary.uniqueSessions ?? totalSessions)} delta={isLive ? null : calcDelta(summary.data?.summary.uniqueSessions ?? 0, summary.data?.priorSummary.uniqueSessions ?? 0)} detail={isLive ? 'Last 30 seconds' : 'Current range'} loading={!isLive && summary.isLoading} />
        <MetricCard label={isLive ? 'Live Events' : 'Events'} value={isLive ? liveWindowEvents : (summary.data?.summary.totalEvents ?? totalEvents)} delta={isLive ? null : calcDelta(summary.data?.summary.totalEvents ?? 0, summary.data?.priorSummary.totalEvents ?? 0)} detail={isLive ? 'Last 30 seconds' : 'Current range'} loading={!isLive && summary.isLoading} />
        <MetricCard label="Crash-Free" value={`${crashFree.toFixed(2)}%`} delta={isLive ? null : calcDelta(summary.data?.summary.crashFreeRate ?? 100, summary.data?.priorSummary.crashFreeRate ?? 100)} loading={summary.isLoading} />
        <MetricCard label={isLive ? 'Apps Now' : 'Active Apps'} value={isLive ? liveActiveApps : (summary.data?.summary.activeApps ?? includedApps.length)} detail={isLive ? 'Last 30 seconds' : comparison.data?.lastEventAt ? `Fresh ${new Date(comparison.data.lastEventAt).toLocaleString()}` : 'No recent events'} loading={!isLive && comparison.isLoading} />
      </div>

      <PortfolioHeatmapHero
        timeRange={timeRange}
        locations={isLive ? liveLocations : (locations.data?.locations ?? [])}
        loading={!isLive && locations.isLoading}
        error={!isLive && locations.error instanceof Error ? locations.error.message : null}
        onRetry={() => locations.refetch()}
        topMovers={topMovers.slice(0, 3)}
        liveEventCount={liveWindowEvents}
      />

      <div className="flex gap-2 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.035] p-1 md:hidden">
        {(['overview', 'map', 'live', 'apps'] as const).map(panel => (
          <button
            key={panel}
            type="button"
            onClick={() => setMobilePanel(panel)}
            className={`mobile-tap-target rounded-xl px-4 text-sm font-semibold capitalize ${mobilePanel === panel ? 'bg-white text-gray-950' : 'text-gray-400'}`}
          >
            {panel}
          </button>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(22rem,0.6fr)]">
        <div className={`space-y-5 ${mobilePanel !== 'overview' && mobilePanel !== 'apps' ? 'hidden md:block' : ''}`}>
          <WidgetShell title="App Comparison" description="Sortable power-user table for portfolio-level investigation.">
            <DataTable rows={apps} columns={columns} getRowKey={(row) => row.appId} onRowClick={(row) => onSelectApp(row.appId)} />
          </WidgetShell>
        </div>

        <aside className={`space-y-5 ${mobilePanel !== 'overview' && mobilePanel !== 'live' ? 'hidden md:block' : ''}`}>
          <WidgetShell title="Live Pulse" description="Realtime stream from the Web PubSub pipeline.">
            <div className="h-[34rem]">
              <LiveEventFeed />
            </div>
          </WidgetShell>
          <WidgetShell title="Platform Mix" description="Android, iOS, and Apple-platform event share in this portfolio range.">
            <div className="space-y-3 p-4">
              {platformMix.map(platform => (
                <div key={platform.platform} className="rounded-2xl border border-white/10 bg-white/[0.035] p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-white">{formatPlatform(platform.platform)}</span>
                    <span className="text-sm tabular-nums text-gray-400">{formatCompactNumber(platform.events)} events</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">{platform.apps} {platform.apps === 1 ? 'app' : 'apps'}</div>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-indigo-300" style={{ width: `${Math.max(4, (platform.events / Math.max(totalEvents, 1)) * 100)}%` }} />
                  </div>
                </div>
              ))}
              {platformMix.length === 0 && <p className="p-3 text-sm text-gray-500">No platform data in this range.</p>}
            </div>
          </WidgetShell>
          <WidgetShell title="Top Movers" description="Apps creating the most signal in this range.">
            <div className="space-y-3 p-4">
              {topMovers.map(app => (
                <button key={app.appId} onClick={() => onSelectApp(app.appId)} className="w-full rounded-2xl border border-white/10 bg-white/[0.035] p-3 text-left hover:bg-white/[0.07]">
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate font-semibold text-white">{app.appName}</span>
                    <span className="text-sm tabular-nums text-gray-400">{formatCompactNumber(app.events)}</span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-300" style={{ width: `${Math.max(6, (app.events / Math.max(totalEvents, 1)) * 100)}%` }} />
                  </div>
                </button>
              ))}
            </div>
          </WidgetShell>
        </aside>
      </div>
    </AnalyticsShell>
  )
}

function PortfolioHeatmapHero({
  timeRange,
  locations,
  loading,
  error,
  onRetry,
  topMovers,
  liveEventCount,
}: {
  timeRange: PortfolioRange
  locations: EventLocation[]
  loading: boolean
  error: string | null
  onRetry: () => void
  topMovers: PortfolioComparisonRow[]
  liveEventCount: number
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const popupRef = useRef<mapboxgl.Popup | null>(null)
  const loadedRef = useRef(false)
  const [mapReady, setMapReady] = useState(false)
  const featureCollection = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: locations.map((location) => ({
      type: 'Feature' as const,
      properties: {
        count: location.count,
        city: location.city,
        country: location.country,
        appsJson: JSON.stringify(location.apps ?? []),
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [location.lng, location.lat],
      },
    })),
  }), [locations])

  useEffect(() => {
    if (!hasMapboxToken || !containerRef.current || mapRef.current) return

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [-20, 25],
      zoom: 1.45,
      projection: { name: 'mercator' },
      attributionControl: false,
    })

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right')

    map.on('load', () => {
      loadedRef.current = true
      setMapReady(true)
      map.resize()
      map.addSource('portfolio-heatmap', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      })
      map.addLayer({
        id: 'portfolio-heat',
        type: 'heatmap',
        source: 'portfolio-heatmap',
        maxzoom: 8,
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'count'], 0, 0, 1000, 1],
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 0.7, 5, 2.3],
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0, 'rgba(15, 23, 42, 0)',
            0.18, 'rgba(79, 70, 229, 0.45)',
            0.42, 'rgba(59, 130, 246, 0.72)',
            0.68, 'rgba(45, 212, 191, 0.82)',
            0.88, 'rgba(250, 204, 21, 0.88)',
            1, 'rgba(244, 114, 182, 0.92)',
          ],
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 18, 5, 42],
          'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 5, 0.92, 8, 0.35],
        },
      })
      map.addLayer({
        id: 'portfolio-points',
        type: 'circle',
        source: 'portfolio-heatmap',
        minzoom: 1,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 1, 4, 5000, 16],
          'circle-color': '#c4b5fd',
          'circle-opacity': 0.72,
          'circle-stroke-color': 'rgba(255,255,255,0.8)',
          'circle-stroke-width': 1,
          'circle-blur': 0.08,
        },
      })

      popupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'portfolio-heatmap-popup',
        maxWidth: '320px',
      })

      map.on('mousemove', 'portfolio-points', (event) => {
        const feature = event.features?.[0]
        if (!feature) return
        map.getCanvas().style.cursor = 'pointer'
        showLocationPopup(map, popupRef.current, feature, event.lngLat)
      })

      map.on('mouseleave', 'portfolio-points', () => {
        map.getCanvas().style.cursor = ''
        popupRef.current?.remove()
      })

      map.on('click', 'portfolio-points', (event) => {
        const feature = event.features?.[0]
        if (!feature) return
        const coordinates = getFeatureCoordinates(feature) ?? event.lngLat
        showLocationPopup(map, popupRef.current, feature, coordinates)
      })
    })

    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      popupRef.current = null
      loadedRef.current = false
      setMapReady(false)
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !loadedRef.current) return
    const source = map.getSource('portfolio-heatmap') as mapboxgl.GeoJSONSource | undefined
    source?.setData(featureCollection)

    // Only reframe the camera for historical ranges, where the data set is
    // fetched once per range. In live mode the clusters change every second and
    // fitBounds starts a 900ms animated flight, so refitting there kept the map
    // permanently mid-animation — burning frames continuously and yanking the
    // view away from anyone trying to pan or zoom.
    if (timeRange === 'live') return
    if (locations.length > 0) fitMapToLocations(map, locations)
  }, [featureCollection, locations, mapReady, timeRange])

  return (
    <section className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.045] shadow-2xl shadow-black/30">
      <div className="flex flex-col gap-3 border-b border-white/10 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="success" dot>Portfolio heatmap</Badge>
            <span className="text-xs font-medium text-gray-500">{timeRangeLabel(timeRange)}</span>
          </div>
          <h2 className="mt-2 text-2xl font-bold tracking-tight text-white md:text-3xl">
            {timeRange === 'live' ? 'Live activity in the last 30 seconds' : 'Global activity by range'}
          </h2>
          <p className="mt-1 text-sm text-gray-400">
            {timeRange === 'live'
              ? 'Realtime geolocated events from every included app in the portfolio.'
              : 'Geolocated event density from every included app in the portfolio.'}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 md:min-w-[26rem]">
          {topMovers.map(app => (
            <button key={app.appId} className="rounded-2xl border border-white/10 bg-black/25 p-3 text-left backdrop-blur transition-colors hover:bg-white/[0.07]">
              <p className="truncate text-sm font-semibold text-white">{app.appName}</p>
              <p className="mt-1 text-xs text-gray-500">{formatCompactNumber(app.events)} events</p>
            </button>
          ))}
        </div>
      </div>
      <div className="relative h-[52vh] min-h-[34rem] overflow-hidden bg-slate-950 md:h-[62vh]">
        {hasMapboxToken && <div ref={containerRef} className="absolute inset-0" />}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/65 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/75 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="rounded-3xl border border-white/10 bg-black/45 p-4 shadow-xl backdrop-blur-xl">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-500">Location clusters</p>
            <p className="mt-1 text-2xl font-bold text-white">{locations.length.toLocaleString()}</p>
            <p className="mt-1 text-xs text-gray-400">
              {timeRange === 'live'
                ? `${liveEventCount.toLocaleString()} events in the live window.`
                : 'Rendered from the analytics location endpoint.'}
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/45 px-3 py-2 text-xs text-gray-300 backdrop-blur-xl">
            Hover or tap a dot to inspect source apps
          </div>
        </div>
        {!hasMapboxToken && (
          <MapState title="Map token missing" message="Set VITE_MAPBOX_TOKEN to render the portfolio heatmap." blocking />
        )}
        {hasMapboxToken && loading && (
          <MapState title="Loading heatmap" message="Fetching geolocated event clusters for the selected range." />
        )}
        {hasMapboxToken && error && (
          <MapState title="Heatmap failed to load" message={error} actionLabel="Retry" onAction={onRetry} />
        )}
        {hasMapboxToken && !loading && !error && locations.length === 0 && (
          <MapState
            title={timeRange === 'live' ? 'Waiting for live pings' : 'No geolocated events'}
            message={timeRange === 'live'
              ? 'The map stays ready while we wait for a fresh event with latitude and longitude in the rolling 30-second window.'
              : 'Events are flowing, but this range has no latitude/longitude clusters yet.'}
          />
        )}
      </div>
    </section>
  )
}

function fitMapToLocations(map: mapboxgl.Map, locations: EventLocation[]) {
  const bounds = new mapboxgl.LngLatBounds()
  locations.forEach(location => bounds.extend([location.lng, location.lat]))
  if (!bounds.isEmpty()) {
    map.fitBounds(bounds, { padding: 80, maxZoom: 4, duration: 900 })
  }
}

function showLocationPopup(
  map: mapboxgl.Map,
  popup: mapboxgl.Popup | null,
  feature: mapboxgl.MapboxGeoJSONFeature,
  lngLat: mapboxgl.LngLatLike
) {
  if (!popup) return

  popup
    .setLngLat(lngLat)
    .setHTML(renderLocationPopup(feature.properties ?? {}))
    .addTo(map)
}

function getFeatureCoordinates(feature: mapboxgl.MapboxGeoJSONFeature): [number, number] | null {
  if (feature.geometry.type !== 'Point') return null
  const coordinates = feature.geometry.coordinates
  if (coordinates.length < 2) return null
  return [coordinates[0], coordinates[1]]
}

function renderLocationPopup(properties: Record<string, unknown>) {
  const count = Number(properties.count ?? 0)
  const city = typeof properties.city === 'string' ? properties.city : ''
  const country = typeof properties.country === 'string' ? properties.country : ''
  const place = [city, country].filter(Boolean).join(', ') || 'Unknown location'
  const apps = parsePopupApps(properties.appsJson)
  const appRows = apps.length > 0
    ? apps.slice(0, 5).map(app => `
      <div style="display:flex;justify-content:space-between;gap:16px;padding:8px 0;border-top:1px solid rgba(255,255,255,0.1);">
        <div style="min-width:0;">
          <div style="font-weight:700;color:white;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(app.appName)}</div>
          <div style="font-size:11px;color:rgba(209,213,219,0.72);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(app.eventName || app.bundleId || 'App telemetry')}</div>
        </div>
        <div style="font-variant-numeric:tabular-nums;color:#c4b5fd;font-weight:700;">${app.count.toLocaleString()}</div>
      </div>
    `).join('')
    : '<div style="padding-top:8px;color:rgba(209,213,219,0.72);">No app attribution available for this cluster.</div>'

  return `
    <div style="min-width:240px;border:1px solid rgba(255,255,255,0.14);border-radius:18px;background:rgba(3,7,18,0.94);padding:14px;box-shadow:0 24px 80px rgba(0,0,0,0.5);backdrop-filter:blur(16px);">
      <div style="font-size:10px;font-weight:800;letter-spacing:0.18em;text-transform:uppercase;color:#34d399;">Heatmap source</div>
      <div style="margin-top:6px;font-size:15px;font-weight:800;color:white;">${escapeHtml(place)}</div>
      <div style="margin-top:2px;margin-bottom:8px;font-size:12px;color:rgba(209,213,219,0.72);">${count.toLocaleString()} ${count === 1 ? 'event' : 'events'}</div>
      ${appRows}
    </div>
  `
}

function parsePopupApps(value: unknown): NonNullable<EventLocation['apps']> {
  if (typeof value !== 'string') return []
  try {
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map(app => ({
        appId: typeof app.appId === 'string' ? app.appId : null,
        appName: typeof app.appName === 'string' ? app.appName : 'Unknown app',
        bundleId: typeof app.bundleId === 'string' ? app.bundleId : null,
        count: Number(app.count) || 0,
        eventName: typeof app.eventName === 'string' ? app.eventName : null,
        lastEventAt: typeof app.lastEventAt === 'string' ? app.lastEventAt : null,
      }))
      .filter(app => app.count > 0)
  } catch {
    return []
  }
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function timeRangeLabel(range: PortfolioRange) {
  return {
    live: 'Live, last 30 seconds',
    '24h': 'Last 24 hours',
    '48h': 'Last 48 hours',
    '72h': 'Last 72 hours',
    '5d': 'Last 5 days',
    '15d': 'Last 15 days',
    '30d': 'Last 30 days',
    '60d': 'Last 60 days',
    '90d': 'Last 90 days',
  }[range]
}

function PortfolioRangePicker({ value, onChange }: { value: PortfolioRange; onChange: (range: PortfolioRange) => void }) {
  const labels: Record<PortfolioRange, string> = {
    live: 'Live',
    '24h': 'Last 24h',
    '48h': 'Last 48h',
    '72h': 'Last 72h',
    '5d': 'Last 5d',
    '15d': 'Last 15d',
    '30d': 'Last 30d',
    '60d': 'Last 60d',
    '90d': 'Last 90d',
  }

  return (
    <div className="flex gap-1 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.045] p-1 custom-scrollbar" role="radiogroup" aria-label="Portfolio range">
      {PORTFOLIO_RANGES.map(range => (
        <button
          key={range}
          type="button"
          onClick={() => onChange(range)}
          aria-pressed={value === range}
          className={`mobile-tap-target shrink-0 rounded-xl px-3.5 py-2 text-sm font-semibold transition-all ${
            value === range
              ? range === 'live'
                ? 'bg-emerald-300 text-emerald-950 shadow-lg shadow-emerald-300/10'
                : 'bg-white text-gray-950 shadow-lg shadow-white/10'
              : 'text-gray-400 hover:bg-white/10 hover:text-white'
          }`}
        >
          <span className="inline-flex items-center gap-2">
            {range === 'live' && <span className={`h-1.5 w-1.5 rounded-full ${value === 'live' ? 'bg-emerald-900' : 'bg-emerald-300'}`} />}
            {labels[range]}
          </span>
        </button>
      ))}
    </div>
  )
}

function MapState({
  title,
  message,
  actionLabel,
  onAction,
  blocking = false,
}: {
  title: string
  message: string
  actionLabel?: string
  onAction?: () => void
  blocking?: boolean
}) {
  return (
    <div className={`${blocking ? 'inset-0 z-10 flex items-center justify-center bg-slate-950/70 p-5 text-center backdrop-blur-sm' : 'bottom-24 left-4 right-4 z-10 flex justify-center px-4 text-left md:bottom-6 md:left-1/2 md:right-auto md:-translate-x-1/2'} absolute pointer-events-none`}>
      <div className={`${blocking ? 'max-w-sm p-6 text-center' : 'max-w-md p-4'} pointer-events-auto rounded-3xl border border-white/10 bg-black/60 shadow-2xl backdrop-blur-xl`}>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-gray-400">{message}</p>
        {actionLabel && onAction && (
          <button type="button" onClick={onAction} className="mt-4 rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-gray-950">
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  )
}
